import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { SuggestionsProvider } from "@/context/SuggestionsContext";
import { ConfirmProvider } from "@/context/ConfirmContext";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

// Unify number formatting app-wide (1,000,000) regardless of the device language
const nativeNumberLocale = Number.prototype.toLocaleString;
// eslint-disable-next-line no-extend-native
Number.prototype.toLocaleString = function toLocaleStringEn(locales, options) {
  const loc = !locales || /^(ar|de|fr|es|it|tr|ru|id|pt|nl)/i.test(String(locales)) ? 'en-US' : locales;
  return nativeNumberLocale.call(this, loc, options);
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <SuggestionsProvider>
        <ConfirmProvider>
          <App />
        </ConfirmProvider>
      </SuggestionsProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);

// ====== Service Worker (PWA) ======
// Register with auto-reload on update so users always get the latest app
// after a deployment (fixes login issues caused by stale cached bundles).
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then((registration) => {
        // Check for updates every time the page loads
        registration.update().catch(() => {});

        // If there's already a waiting SW, prompt skipWaiting
        if (registration.waiting) {
          registration.waiting.postMessage('SKIP_WAITING');
        }

        // Listen for new SW installations
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // A new version is ready — activate it immediately
              newWorker.postMessage('SKIP_WAITING');
            }
          });
        });
      })
      .catch((err) => {
        console.warn('[SW] Registration failed:', err);
      });

    // Reload the page when the active SW changes (after skipWaiting)
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  });
}
